import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, Edit, Save, X, Lock, Camera, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';

const ClientProfile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [clientData, setClientData] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
    fetchClientData();
  }, [user]);

  const fetchClientData = async () => {
    try {
      const response = await api.get('/client/profile');
      setClientData(response.data);
    } catch (error) {
      console.error('Failed to fetch client data:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put('/auth/profile', formData);
      updateUser(response.data);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || ''
    });
    setIsEditing(false);
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordDialogOpen(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="profile-title">My Profile</h2>
          <p className="text-gray-600">Manage your account information</p>
        </div>
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            variant="outline"
            data-testid="edit-profile-button"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={handleCancel}
              variant="outline"
              data-testid="cancel-edit-button"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              data-testid="save-profile-button"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="glass rounded-xl p-8">
        {/* Profile Header */}
        <div className="flex items-center space-x-6 mb-8 pb-8 border-b border-gray-200">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              {user?.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt="Profile" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-3xl font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              )}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {user?.first_name} {user?.last_name}
            </h3>
            <p className="text-gray-500">{user?.email}</p>
            {clientData?.company_name && (
              <p className="text-teal-600 font-medium mt-1">{clientData.company_name}</p>
            )}
          </div>
        </div>

        {/* Profile Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Personal Information */}
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-teal-600" />
              Personal Information
            </h4>
            
            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                First Name
              </Label>
              {isEditing ? (
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Enter first name"
                  data-testid="first-name-input"
                />
              ) : (
                <p className="text-gray-900 font-medium py-2">{user?.first_name || 'Not provided'}</p>
              )}
            </div>

            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                Last Name
              </Label>
              {isEditing ? (
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Enter last name"
                  data-testid="last-name-input"
                />
              ) : (
                <p className="text-gray-900 font-medium py-2">{user?.last_name || 'Not provided'}</p>
              )}
            </div>

            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                Phone Number
              </Label>
              {isEditing ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                  data-testid="phone-input"
                />
              ) : (
                <p className="text-gray-900 font-medium py-2">{user?.phone || 'Not provided'}</p>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Building className="w-5 h-5 text-teal-600" />
              Account Information
            </h4>
            
            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                <Mail className="w-4 h-4 mr-2" />
                Email Address
              </Label>
              <p className="text-gray-900 font-medium py-2">{user?.email}</p>
              <p className="text-xs text-gray-500">Contact support to change your email</p>
            </div>

            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                Account Status
              </Label>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                <Check className="w-4 h-4" />
                {user?.status || 'Active'}
              </span>
            </div>

            <div>
              <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                Member Since
              </Label>
              <p className="text-gray-900 font-medium py-2">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </p>
            </div>

            {clientData?.company_name && (
              <div>
                <Label className="flex items-center text-sm font-medium text-gray-500 mb-2">
                  <Building className="w-4 h-4 mr-2" />
                  Company
                </Label>
                <p className="text-gray-900 font-medium py-2">{clientData.company_name}</p>
                {clientData.business_type && (
                  <p className="text-sm text-gray-500">{clientData.business_type}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Security Section */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-teal-600" />
            Security
          </h4>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Password</p>
              <p className="text-sm text-gray-500">Change your account password</p>
            </div>
            <Button
              onClick={() => setPasswordDialogOpen(true)}
              variant="outline"
              data-testid="change-password-button"
            >
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </div>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Enter current password"
                className="mt-1"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password"
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="mt-1"
                data-testid="confirm-new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProfile;
